/**
 * Attaky Color Preset System
 * 22 preset colorways + grouping logic + rendering utilities
 */

// Preset colorways are managed through storefront content data
var ATTAKY_PRESETS = [];

// Preset groups are managed through storefront content data
var ATTAKY_PRESET_GROUPS = [];

// Utility functions
var AttakyPresetUtils = {

  // Canonical display order — determines sort order for collections, couples, and individual themes
  SERIES_ORDER: [
    'The Core Collections',
    'The Tactical Collections',
    'Special Edition Collections',
    'Theme & Duo Collections'
  ],
  COUPLE_ORDER: ['Cafe', 'Dreamscape', 'Arcade', 'Pastel', 'Chaos', 'Gothic'],
  THEME_ORDER: [
    // The Core Collections
    'Ceramic White', 'Industrial Grey', 'Carbon Black',
    // The Tactical Collections
    'Ranger Green', 'Deep Navy', 'Coyote Tan', 'Hazard Orange', 'Marine White',
    // Special Edition Collections
    'Urban Jungle', 'Summer Pop',
    // Theme & Duo Collections — Cafe couple
    'Latte', 'Espresso',
    // Dreamscape couple
    'Unicorn Pink', 'Unicorn Purple',
    // Arcade couple
    'Player 1 (Neon Red)', 'Player 2 (Neon Blue)',
    // Pastel couple
    'Cotton Candy', 'Bubblegum Blue',
    // Chaos couple
    'Jester Purple', 'Mischief Red',
    // Gothic couple
    'Gothic Violet', 'Abyss Black'
  ],

  /**
   * Sort an array so canonical items come first (in canonical order),
   * unknown items follow in their original insertion order.
   */
  sortByCanonical: function(arr, canonicalList, keyFn) {
    return arr.slice().sort(function(a, b) {
      var ia = canonicalList.indexOf(keyFn(a));
      var ib = canonicalList.indexOf(keyFn(b));
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  },

  /**
   * Keep one color entry per color ID when a product has additional options.
   */
  dedupeVariantColors: function(entries) {
    var seen = {};
    return (entries || []).filter(function(entry) {
      if (!entry || !entry.id) return false;
      var key = entry.id.toLowerCase();
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  },

  normalizeOptionValue: function(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  },

  readDataArray: function(container, attributeName) {
    var raw = container && container.getAttribute(attributeName);
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  },

  shouldUsePrimaryProduct: function(colorValues, primaryProductUrl) {
    if (!primaryProductUrl) return false;
    var self = this;
    return !(colorValues || []).some(function(value) {
      var normalized = self.normalizeOptionValue(value);
      return normalized === 'studio config' || normalized === 'lab access';
    });
  },

  variantOptionValue: function(variant, position) {
    if (!variant) return '';
    if (Array.isArray(variant.options)) return variant.options[position - 1] || '';
    return variant['option' + position] || '';
  },

  findPrimaryVariant: function(sourceVariant, sourceOptionNames, primaryOptionNames, primaryVariants) {
    if (!sourceVariant) return null;

    var self = this;
    var sourceSelections = {};
    (sourceOptionNames || []).forEach(function(optionName, index) {
      var name = self.normalizeOptionValue(optionName);
      var value = self.normalizeOptionValue(self.variantOptionValue(sourceVariant, index + 1));
      if (name && value) sourceSelections[name] = value;
    });

    var sharedPositions = [];
    (primaryOptionNames || []).forEach(function(optionName, index) {
      if (sourceSelections[self.normalizeOptionValue(optionName)] != null) {
        sharedPositions.push(index);
      }
    });
    if (!sharedPositions.length) return null;

    var matches = (primaryVariants || []).filter(function(variant) {
      return sharedPositions.every(function(index) {
        var optionName = self.normalizeOptionValue(primaryOptionNames[index]);
        var targetValue = self.normalizeOptionValue(self.variantOptionValue(variant, index + 1));
        return targetValue === sourceSelections[optionName];
      });
    });

    return matches.length === 1 ? matches[0] : null;
  },

  /**
   * Find a preset by ID (case-insensitive)
   */
  findPreset: function(id) {
    if (!id) return null;
    var lowerID = id.toLowerCase();
    return ATTAKY_PRESETS.find(function(p) {
      return p.id.toLowerCase() === lowerID;
    }) || null;
  },

  /**
   * Render a single MiniThemeCard HTML string
   * @param {Object} colors - { main, keyBase, keyText, logo }
   * @param {boolean} isSelected
   * @param {number} size - pixel size (default 24)
   * @returns {string} HTML string
   */
  renderMiniThemeCard: function(colors, isSelected, size, name) {
    size = size || 32;
    var splitGradient = 'linear-gradient(225deg, ' + colors.keyText + ' 50%, ' + colors.logo + ' 50%)';
    var selectedClass = isSelected ? ' mini-theme-card--selected' : '';
    var titleAttr = name ? ' title="' + name + '"' : '';
    return '<button class="mini-theme-card' + selectedClass + '" style="width:' + size + 'px;height:' + size + 'px"' + titleAttr + '>' +
      '<span class="mini-theme-card__left" style="background-color:' + colors.main + '"></span>' +
      '<span class="mini-theme-card__right">' +
        '<span class="mini-theme-card__right-top" style="background-color:' + colors.keyBase + '"></span>' +
        '<span class="mini-theme-card__right-bottom" style="background:' + splitGradient + '"></span>' +
      '</span>' +
    '</button>';
  },

  /**
   * Resolve a preset by ID, preferring product color data before the default presets
   * @param {string} id - color name / preset ID
   * @param {Array|null} variantColorsFromMeta - variant color data, or null
   * @returns {Object|null} preset object { id, name, colors }
   */
  resolvePreset: function(id, variantColorsFromMeta) {
    if (!id) return null;
    var lowerID = id.toLowerCase();
    // 1. Priority: product color data
    if (variantColorsFromMeta && variantColorsFromMeta.length) {
      var metaMatch = variantColorsFromMeta.find(function(m) {
        return m.id.toLowerCase() === lowerID;
      });
      if (metaMatch) return { id: metaMatch.id, name: metaMatch.id, colors: metaMatch.colors };
    }
    // 2. Fallback: hardcoded presets
    return this.findPreset(id);
  },

  /**
   * Render grouped color swatches for a product card
   * @param {HTMLElement} container - DOM element to render into
   * @param {Array} variantColorValues - array of color option values from product variants
   * @param {Function} onSelect - callback(presetId) when a swatch is clicked
   * @param {string} selectedId - currently selected preset ID
   * @param {Array|null} variantColorsFromMeta - variant color data, or null
   */
  renderColorSwatches: function(container, variantColorValues, onSelect, selectedId, variantColorsFromMeta) {
    if (!container) return;

    variantColorsFromMeta = this.dedupeVariantColors(variantColorsFromMeta);

    // Convert variant color values to lowercase for matching
    var availableColors = variantColorValues.map(function(v) { return v.toLowerCase(); });

    var html = '';
    var self = this;

    // Header with theme name
    var selectedPreset = this.resolvePreset(selectedId, variantColorsFromMeta);
    html += '<div class="color-swatches__header">';
    html += '<span class="color-swatches__theme-label">Theme:</span>';
    html += '<span class="color-swatches__theme-name">' + (selectedPreset ? selectedPreset.name : '') + '</span>';
    html += '</div>';

    // Track grouped colors so ungrouped product colors can be rendered later
    var renderedColors = {};

    // Render each group
    ATTAKY_PRESET_GROUPS.forEach(function(group) {
      html += '<div>';
      html += '<div class="color-swatches__group-label">' + group.label + '</div>';
      html += '<div class="color-swatches__row">';

      group.ids.forEach(function(item) {
        if (typeof item === 'object' && item.ids) {
          // Couple pair - wrap in bordered container
          html += '<div class="color-swatches__couple" title="' + item.label + '">';
          html += '<div class="color-swatches__couple-label">' + item.label + '</div>';
          html += '<div class="color-swatches__couple-items">';
          item.ids.forEach(function(subId) {
            var preset = self.resolvePreset(subId, variantColorsFromMeta);
            if (preset && availableColors.indexOf(subId.toLowerCase()) !== -1) {
              renderedColors[subId.toLowerCase()] = true;
              html += '<div data-preset-id="' + preset.id + '" class="color-swatches__item">';
              html += self.renderMiniThemeCard(preset.colors, selectedId === preset.id, 32, preset.name);
              html += '<span class="color-swatches__preset-name">' + preset.name + '</span>';
              html += '</div>';
            }
          });
          html += '</div>';
          html += '</div>';
        } else {
          // Single preset
          var preset = self.resolvePreset(item, variantColorsFromMeta);
          if (preset && availableColors.indexOf(item.toLowerCase()) !== -1) {
            renderedColors[item.toLowerCase()] = true;
            html += '<div data-preset-id="' + preset.id + '" class="color-swatches__item">';
            html += self.renderMiniThemeCard(preset.colors, selectedId === preset.id, 32, preset.name);
            html += '<span class="color-swatches__preset-name">' + preset.name + '</span>';
            html += '</div>';
          }
        }
      });

      html += '</div>';
      html += '</div>';
    });

    // Render product colors that do not belong to a default group
    if (variantColorsFromMeta && variantColorsFromMeta.length) {
      var ungrouped = variantColorsFromMeta.filter(function(m) {
        return !renderedColors[m.id.toLowerCase()] && availableColors.indexOf(m.id.toLowerCase()) !== -1;
      });
      if (ungrouped.length) {
        // Group by meta.group (collection)
        var metaGroups = {};
        var metaGroupOrder = [];
        ungrouped.forEach(function(meta) {
          var g = meta.group || '';
          if (!metaGroups[g]) { metaGroups[g] = []; metaGroupOrder.push(g); }
          metaGroups[g].push(meta);
        });
        // Sort collections by canonical order; unknown collections go at the end in original order
        metaGroupOrder = self.sortByCanonical(metaGroupOrder, self.SERIES_ORDER, function(k) { return k; });

        metaGroupOrder.forEach(function(groupKey) {
          var groupItems = metaGroups[groupKey];
          html += '<div>';
          html += '<div class="color-swatches__group-label">' + (groupKey || 'New Colors') + '</div>';
          html += '<div class="color-swatches__row">';

          // Within each series group, pair items sharing the same couple value
          var coupleGroups = {};
          var coupleOrder = [];
          groupItems.forEach(function(meta) {
            var c = meta.couple || '';
            if (!coupleGroups[c]) { coupleGroups[c] = []; coupleOrder.push(c); }
            coupleGroups[c].push(meta);
          });
          // Sort couples by canonical order; unknown couples go at the end
          coupleOrder = self.sortByCanonical(coupleOrder, self.COUPLE_ORDER, function(k) { return k; });

          coupleOrder.forEach(function(coupleKey) {
            var coupleItems = coupleGroups[coupleKey];
            // Sort themes within each couple/single by canonical order
            coupleItems = self.sortByCanonical(coupleItems, self.THEME_ORDER, function(m) { return m.id; });
            if (coupleKey && coupleItems.length > 1) {
              html += '<div class="color-swatches__couple" title="' + coupleKey + '">';
              html += '<div class="color-swatches__couple-label">' + coupleKey + '</div>';
              html += '<div class="color-swatches__couple-items">';
              coupleItems.forEach(function(meta) {
                renderedColors[meta.id.toLowerCase()] = true;
                html += '<div data-preset-id="' + meta.id + '" class="color-swatches__item">';
                html += self.renderMiniThemeCard(meta.colors, selectedId === meta.id, 32, meta.id);
                html += '<span class="color-swatches__preset-name">' + meta.id + '</span>';
                html += '</div>';
              });
              html += '</div>';
              html += '</div>';
            } else {
              coupleItems.forEach(function(meta) {
                renderedColors[meta.id.toLowerCase()] = true;
                html += '<div data-preset-id="' + meta.id + '" class="color-swatches__item">';
                html += self.renderMiniThemeCard(meta.colors, selectedId === meta.id, 32, meta.id);
                html += '<span class="color-swatches__preset-name">' + meta.id + '</span>';
                html += '</div>';
              });
            }
          });

          html += '</div>';
          html += '</div>';
        });
      }
    }

    // Build Your Colorway button — filter out white/near-white from border gradient
    var gradientColors = (variantColorsFromMeta || []).map(function(m) { return m.colors.main; }).filter(function(c) { return c && c.toUpperCase() !== '#FFFFFF' && c.toUpperCase() !== '#FFF'; });
    var primaryProductUrl = container.getAttribute('data-primary-product-url') || '';
    var usesPrimaryProduct = this.shouldUsePrimaryProduct(variantColorValues, primaryProductUrl);
    var colorwayLabel = usesPrimaryProduct ? 'Explore Custom Colorways' : 'Build Your Colorway!';
    var buildHref = primaryProductUrl;
    if (!usesPrimaryProduct) {
      buildHref = '/pages/build';
      var buildParams = [];
      var productHandle = container.getAttribute('data-product-handle');
      if (productHandle) buildParams.push('handle=' + encodeURIComponent(productHandle));
      if (selectedId) buildParams.push('color=' + encodeURIComponent(selectedId));
      if (buildParams.length) buildHref += '?' + buildParams.join('&');
    }
    html += '<a href="' + buildHref + '" class="btn-colorway" data-build-colorway-link style="background-image:linear-gradient(white,white),linear-gradient(90deg,' + gradientColors.join(', ') + ');background-origin:border-box;background-clip:padding-box,border-box">';
    html += '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>';
    html += ' ' + colorwayLabel;
    html += '</a>';

    container.innerHTML = html;

    // Bind click events
    var swatchButtons = container.querySelectorAll('[data-preset-id]');
    swatchButtons.forEach(function(el) {
      el.addEventListener('click', function() {
        var presetId = el.getAttribute('data-preset-id');
        if (onSelect) onSelect(presetId);
      });
    });
  },

  syncBuildVariant: function(container, variantId) {
    var link = container && container.querySelector('[data-build-colorway-link]');
    if (!link) return;

    var primaryProductUrl = container.getAttribute('data-primary-product-url') || '';
    var colorValues = this.readDataArray(container, 'data-color-values');
    var usesPrimaryProduct = this.shouldUsePrimaryProduct(colorValues, primaryProductUrl);
    var baseHref = usesPrimaryProduct ? primaryProductUrl : link.getAttribute('href');
    var url = new URL(baseHref || '/pages/build', window.location.origin);

    if (usesPrimaryProduct) {
      var sourceVariants = this.readDataArray(container, 'data-variants');
      var sourceVariant = sourceVariants.find(function(variant) {
        return String(variant && variant.id) === String(variantId);
      }) || null;
      var primaryVariant = this.findPrimaryVariant(
        sourceVariant,
        this.readDataArray(container, 'data-product-options'),
        this.readDataArray(container, 'data-primary-product-options'),
        this.readDataArray(container, 'data-primary-product-variants')
      );
      url.searchParams.delete('variant');
      if (primaryVariant) url.searchParams.set('variant', String(primaryVariant.id));
    } else if (variantId) {
      url.searchParams.set('variant', String(variantId));
    } else {
      url.searchParams.delete('variant');
    }
    link.setAttribute('href', url.pathname + url.search + url.hash);
  },

  /**
   * Render all presets (no filtering by variant) - for pages like hero
   */
  renderAllSwatches: function(container, onSelect, selectedId) {
    var allIds = ATTAKY_PRESETS.map(function(p) { return p.id; });
    this.renderColorSwatches(container, allIds, onSelect, selectedId);
  }
};
